/**
 * The links block: which of the panel's columns open a URL, and where the URL comes from. A link
 * is a TAG on a column, orthogonal to its value type — a person-name column stays a string; this
 * block says its cells also navigate. Keyed by the displayed column, naming the row field that
 * holds the URL (`urlKey`, dot paths allowed, and it may name the column itself when the values
 * ARE the urls), plus how it opens (`target`: new tab unless "self").
 *
 * Declared per column at write time, never sniffed from the value: the renderer used to linkify
 * any cell whose string started with http(s), which violated the formatting contract's "no render
 * site infers from a value" and was configurable by nobody (deleted 2026-09-01 when this block
 * shipped). A URL-valued column is plain text until a link is declared for it.
 *
 * Map-shaped like `format` and for the same reasons: one column is linked without resending the
 * others, a key naming a column the panel does not draw is inert, and another renderer adopts the
 * feature by joining `carriedBy` with no schema work. Unlike `format` it IS gated: only the table
 * draws it today, and storing a link no renderer reads is the silent no-op the gate exists to
 * prevent.
 */

export const ANALYTICS_LINK_TARGETS = ['blank', 'self'];

/* Matches the labeledBy cap — a urlKey is the same kind of row-field pointer. */
const URL_KEY_MAX_CHARS = 120;

const isPlainObject = (value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value);

/* One stored entry, normalized: `target` is kept only when it is "self", so the stored config says
   nothing where the default (a new tab) applies. */
const toLinkEntry = (entry) => {
  const urlKey = String(entry?.urlKey || '')
    .trim()
    .slice(0, URL_KEY_MAX_CHARS);

  if (!urlKey) {
    return null;
  }

  return { urlKey, ...(entry?.target === 'self' ? { target: 'self' } : {}) };
};

/* Render-side reading of a stored block: tolerant, malformed entries dropped — a stored config is
   tolerated like any other, and the honest rendering of a broken link declaration is no link. */
export const normalizeAnalyticsLinksBlock = (block) => {
  if (!isPlainObject(block)) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(block)
      .map(([key, entry]) => [key, toLinkEntry(entry)])
      .filter(([key, entry]) => key && entry)
  );
};

/* Write-side check, shared by the tool and display code — a rejected write teaches the caller,
   where a silently dropped entry would read as saved. `allowRemovals` accepts ''/null values,
   which is how the merging tool path says "take this column's link off"; display code returns the
   whole block fresh each resolution, so a removal token there is a typo. */
export const validateAnalyticsLinksBlock = (block, { allowRemovals = false } = {}) => {
  if (!isPlainObject(block)) {
    return 'links must be an object keyed by column name, like { "owner_name": { "urlKey": "profile_url" } }.';
  }

  for (const [key, entry] of Object.entries(block)) {
    if (!key) {
      return 'Every links key must be a column name.';
    }

    if (allowRemovals && (entry === '' || entry === null)) {
      continue;
    }

    if (!isPlainObject(entry) || typeof entry.urlKey !== 'string' || !entry.urlKey.trim()) {
      return `links["${key}"] needs a urlKey — the row field holding the URL, like { "urlKey": "profile_url" }.`;
    }

    if (entry.target !== undefined && !ANALYTICS_LINK_TARGETS.includes(entry.target)) {
      return `links["${key}"].target must be one of: ${ANALYTICS_LINK_TARGETS.join(', ')}. Omit it for a new tab.`;
    }
  }

  return '';
};

/* The merge, hand-written because the block is a map (see format.js, whose semantics this
   mirrors): a caller links ONE column without resending the others, and ''/null takes a column's
   link off. Assumes the requested block already passed validateAnalyticsLinksBlock. */
export const mergeAnalyticsLinks = ({ requested, stored } = {}) => {
  const merged = normalizeAnalyticsLinksBlock(stored);

  for (const [key, entry] of Object.entries(isPlainObject(requested) ? requested : {})) {
    if (!key) {
      continue;
    }

    if (entry === '' || entry === null) {
      delete merged[key];
      continue;
    }

    const normalized = toLinkEntry(entry);

    if (normalized) {
      merged[key] = normalized;
    }
  }

  return merged;
};

/* The one scheme guard, applied where the anchor is built: http(s) or nothing. A row value is
   data, so this cannot be validated at write time — and a javascript: or data: value rendering as
   a live anchor is the injection this closes off. Not inference: the link is declared; only the
   VALUE is checked for being usable as one. */
export const toSafeAnalyticsLinkHref = (value) => {
  const href = String(value ?? '').trim();

  return /^https?:\/\//i.test(href) ? href : '';
};

const links = {
  /* No names: a block, not a renderer. */
  names: [],
  block: 'links',
  /* Not one type's settings — the tool declares it by hand beside `format` and the per-type loops
     skip it. Gated to the renderers that draw it, unlike format, because a type that shows no
     cells has nothing to link. */
  sharedBlock: true,
  carriedBy: ['table'],
  /* No `fields`: a map keyed by the panel's own column names, which the field-spec vocabulary
     cannot describe. Merge and validation are hand-written above, next to format's. */
  toolSchema: {
    type: ['object', 'null'],
    additionalProperties: {
      type: ['object', 'null'],
      additionalProperties: false,
      required: ['urlKey'],
      properties: {
        urlKey: { type: 'string', description: 'The row field holding the URL (dot paths allowed). May be a field the columns list hides — keep it in the rows via SQL — or the linked column itself when its values are the URLs.' },
        target: { type: ['string', 'null'], enum: [...ANALYTICS_LINK_TARGETS, null], description: 'How the link opens: omit for a new tab (the default); "self" for the same tab.' }
      }
    },
    description: 'Table only: which columns\' cells open a URL, keyed by the displayed column — {"owner_name": {"urlKey": "profile_url"}}. The cell keeps its normal value and type; it acts as a link on hover and click. THE answer to "make these clickable / link each row to its record" — a column of raw URLs is plain text until declared here too ({"website": {"urlKey": "website"}}). Only http(s) values render as links; a row whose urlKey value is empty or not a URL renders that cell as plain text. Merged over what is stored, so you can link one column without resending the others; send null for a column to take its link back off.'
  }
};

export default links;
